import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          action?: string;
          theme?: 'light' | 'dark' | 'auto';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => string | number;
      reset: (widgetId?: string | number) => void;
      remove: (widgetId?: string | number) => void;
    };
  }
}

export interface TurnstileWidgetHandle {
  reset: () => void;
}

interface TurnstileWidgetProps {
  onTokenChange: (token: string) => void;
  siteKey?: string;
  /** Turnstile action; the backend rejects tokens solved for a different one. */
  action?: string;
}

const TURNSTILE_SCRIPT_ID = 'cloudflare-turnstile-script';
const DEFAULT_SITE_KEY = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_SITE_KEY || '';

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ onTokenChange, siteKey = DEFAULT_SITE_KEY, action }, ref) => {
    const widgetRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | number | null>(null);
    const [ready, setReady] = useState(false);
    const [loadError, setLoadError] = useState('');

    useImperativeHandle(ref, () => ({
      reset: () => {
        onTokenChange('');
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }));

    useEffect(() => {
      if (!siteKey) return;

      setLoadError('');

      const renderWidget = () => {
        if (!window.turnstile || !widgetRef.current || widgetIdRef.current !== null) {
          return;
        }

        widgetIdRef.current = window.turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          action,
          theme: 'auto',
          callback: (token: string) => {
            onTokenChange(token);
          },
          'expired-callback': () => {
            onTokenChange('');
            if (widgetIdRef.current !== null) {
              window.turnstile?.reset(widgetIdRef.current);
            }
          },
          'error-callback': () => {
            onTokenChange('');
            setLoadError('Verification widget could not be loaded. Please refresh and try again.');
          },
        });
        setReady(true);
      };

      const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;

      if (existingScript) {
        if (window.turnstile) {
          renderWidget();
        } else {
          existingScript.addEventListener('load', renderWidget, { once: true });
          existingScript.addEventListener(
            'error',
            () => {
              setLoadError('Verification widget could not be loaded. Please refresh and try again.');
            },
            { once: true }
          );
        }
      } else {
        const script = document.createElement('script');
        script.id = TURNSTILE_SCRIPT_ID;
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.onload = renderWidget;
        script.onerror = () => {
          setLoadError('Verification widget could not be loaded. Please refresh and try again.');
        };
        document.head.appendChild(script);
      }

      return () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            // Ignore widget cleanup errors during unmount.
          }
          widgetIdRef.current = null;
        }
      };
    }, [onTokenChange, siteKey, action]);

    if (!siteKey) {
      return (
        <div className="space-y-2 rounded-xl border border-slate-200/70 bg-white/70 px-3 py-3 shadow-xs backdrop-blur-xs">
          <div className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-400" aria-hidden="true" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
              Security check
            </p>
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] font-medium text-slate-500">
              Required
            </span>
          </div>

          <div className="rounded-lg border border-slate-200/80 bg-slate-50/80 px-3 py-2">
            <p className="text-xs font-medium text-slate-700">
              Verification is temporarily unavailable.
            </p>
            <p className="mt-0.5 text-[11px] text-slate-500">
              Please refresh the page or contact support.
            </p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-2 rounded-xl border border-slate-200/70 bg-white/70 px-3 py-3 shadow-xs backdrop-blur-xs">
        <div className="flex items-center gap-2">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden="true" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500">
            Security check
          </p>
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
            Protected
          </span>
        </div>

        <div className="inline-flex rounded-lg border border-slate-200/80 bg-white px-2 py-2">
          <div ref={widgetRef} />
        </div>

        {!ready && !loadError && (
          <p className="text-[11px] text-slate-400">
            {import.meta.env.PROD ? 'Loading verification...' : 'Loading verification widget...'}
          </p>
        )}
        {loadError && (
          <p className="text-[11px] text-amber-700">
            {import.meta.env.PROD
              ? loadError
              : 'Verification widget could not be loaded. Please refresh and try again.'}
          </p>
        )}
      </div>
    );
  }
);

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
